import { useEffect, useLayoutEffect, useState } from 'react';

// Matches the max-h-60 design cap on the dropdown containers.
const MAX_DROPDOWN_HEIGHT = 240;
// Clearance kept between the dropdown and the recording bar so the last
// option isn't flush against it.
const OFFSET = 12;

function getFloor(): number {
  const bar = document.querySelector('[data-recording-bottom-bar]');
  return bar ? bar.getBoundingClientRect().top : window.innerHeight;
}

function getScrollParent(element: HTMLElement | null): HTMLElement | null {
  for (let node = element?.parentElement ?? null; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

// Keeps an open dropdown from being truncated near the bottom of the field: scrolls the field up
// (before paint) so a full-height dropdown fits between the caret and the recording bar, and only
// falls back to capping the returned height when the field can't scroll far enough.
export function useDropdownFit(
  getAnchorBottom: () => number | null,
  isOpen: boolean,
  getScrollTarget: () => HTMLElement | null,
  anchorKey: unknown
): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  // Reset on (re)open/move so a stale cap from a previous position can't briefly truncate the list.
  useEffect(() => setMaxHeight(undefined), [isOpen, anchorKey]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const anchorBottom = getAnchorBottom();
    if (anchorBottom === null) return;
    const overflow = anchorBottom + OFFSET + MAX_DROPDOWN_HEIGHT - getFloor();
    if (overflow <= 0) return;
    const scroller = getScrollParent(getScrollTarget());
    (scroller ?? window).scrollBy({ top: overflow, behavior: 'smooth' });
  }, [isOpen, anchorKey, getAnchorBottom, getScrollTarget]);

  useEffect(() => {
    if (!isOpen) return;
    const recompute = () => {
      const anchorBottom = getAnchorBottom();
      if (anchorBottom === null) return;
      const available = getFloor() - anchorBottom - OFFSET;
      setMaxHeight(Math.max(0, Math.min(MAX_DROPDOWN_HEIGHT, available)));
    };

    let frame: number | null = null;
    const scheduleRecompute = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        recompute();
      });
    };

    recompute();
    window.addEventListener('resize', scheduleRecompute);
    // Capture phase so scrolling of any inner container is caught too.
    window.addEventListener('scroll', scheduleRecompute, true);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleRecompute);
      window.removeEventListener('scroll', scheduleRecompute, true);
    };
  }, [getAnchorBottom, isOpen]);

  return maxHeight;
}
